import { classes } from "@grindex-common/helpers/cssGlobals";
import { safeParseNumber } from "@grindex-common/helpers/numberHelpers";
import { isNullish, isNullishOrEmpty } from "./typeHelpers";

function removeClass($elements, className) {
	const classArray = className.split(" ");
	if ($elements) {
		$elements.forEach(($element) => {
			classArray.forEach((cls) => $element.classList.remove(cls));
		});
	}
}

function addClass($elements, className) {
	if ($elements) {
		const classArray = className.split(" ");
		$elements.forEach(($element) => {
			classArray.forEach((cls) => $element.classList.add(cls));
		});
	}
}

function hideElement($element) {
	if ($element && $element.classList) {
		$element.classList.add(classes.HIDDEN);

		$element.style.display = "none";
	}
}

function showElement($element, displayClass, ignoreDisplayClass) {
	if ($element && $element.classList) {
		$element.classList.remove(classes.HIDDEN);

		if ($element.style.display === "none") {
			$element.style.display = displayClass ? displayClass : "block";
		} else if ($element.style.display === "" && !ignoreDisplayClass) {
			$element.style.display = displayClass ? displayClass : "block";
		}
	}
}

function hideElements(selector, $container) {
	const $module = $container ? $container : document;
	$module.querySelectorAll(selector).forEach(($el) => {
		hideElement($el);
	});
}

function outerHeight($element) {
	if (!$element) {
		return 0;
	}

	let elementHeight = $element.offsetHeight;
	const style = getComputedStyle($element);

	elementHeight += parseInt(style.marginTop) + parseInt(style.marginBottom);
	return elementHeight;
}

function height($element) {
	if (!$element) {
		return 0;
	}

	return parseFloat(getComputedStyle($element, null).height.replace("px", ""));
}

function width($element) {
	if (!$element) {
		return 0;
	}

	const computedStyle = getComputedStyle($element, null);

	return computedStyle ? parseFloat(computedStyle.width.replace("px", "")) : 0;
}

function showElements(selector, $container) {
	const $module = $container ? $container : document;
	$module.querySelectorAll(selector).forEach(($el) => {
		showElement($el);
	});
}

function getNextSibling($element, selector) {
	if (!$element) {
		return null;
	}

	let $sibling = $element.nextElementSibling;

	if (!selector) {
		return $sibling;
	}

	while ($sibling) {
		if ($sibling.matches(selector)) {
			return $sibling;
		}
		$sibling = $sibling.nextElementSibling;
	}

	return null;
}

function getPreviousSibling($element, selector) {
	if (!$element) {
		return null;
	}

	let $sibling = $element.previousElementSibling;

	if (!selector) {
		return $sibling;
	}

	while ($sibling) {
		if ($sibling.matches(selector)) {
			return $sibling;
		}
		$sibling = $sibling.previousElementSibling;
	}

	return null;
}

function getSiblings(e, className) {
	const siblings = [];

	if (!e.parentNode) {
		return siblings;
	}

	let sibling = e.parentNode.firstChild;

	while (sibling) {
		if (sibling.nodeType === 1 && sibling !== e) {
			if (!className || sibling.classList.contains(className)) {
				siblings.push(sibling);
			}
		}
		sibling = sibling.nextSibling;
	}
	return siblings;
}

function getSiblingsByType(e, type) {
	const siblings = [];

	if (!e.parentNode) {
		return siblings;
	}

	let sibling = e.parentNode.firstChild;

	while (sibling) {
		if (sibling.nodeType === 1 && sibling !== e) {
			if (isTagOfType(sibling, type)) {
				siblings.push(sibling);
			}
		}
		sibling = sibling.nextSibling;
	}
	return siblings;
}

/**
 * Adds an event listener to a single HTMLElement or a collection of HTMLElements.
 * @template {keyof HTMLElementEventMap | string & {}} T
 * @param {EventTarget | EventTarget[] | NodeList | HTMLCollection | null } $element
 * @param {T} action
 * @param { (e: T extends keyof HTMLElementEventMap ? HTMLElementEventMap[T]: unknown) => void} callback
 */
function addListener($element, action, callback) {
	if (!$element) {
		return;
	}

	const isCollection = $element instanceof NodeList || $element instanceof HTMLCollection || Array.isArray($element);

	if (isCollection) {
		$element.forEach(($el) => {
			$el.addEventListener(action, callback);
		});
	} else {
		$element.addEventListener(action, callback);
	}
}

function toggleVisibility($element) {
	if ($element.style.display === "none") {
		$element.style.display = "block";
	} else {
		$element.style.display = "none";
	}
}

function isVisible($element) {
	const clientRect = $element.getBoundingClientRect();
	let computedStyle;

	if (
		clientRect.width > 0 &&
		clientRect.height > 0 &&
		clientRect.left + clientRect.width > 0 &&
		clientRect.right - clientRect.width < window.outerWidth &&
		clientRect.top + clientRect.height > 0 &&
		clientRect.bottom - clientRect.width < window.outerHeight &&
		(computedStyle = window.getComputedStyle($element)) &&
		computedStyle.getPropertyValue("visibility") === "visible" &&
		computedStyle.getPropertyValue("opacity") !== "none"
	) {
		return true;
	}
	return false;
}

function getVisibleElements($elements) {
	const matches = [];

	for (let i = 0; i < $elements.length; ++i) {
		if (isVisible($elements[i])) {
			matches.push($elements[i]);
		}
	}

	return matches;
}

function getParents($element, parentSelector) {
	const selector = typeof parentSelector === "undefined" ? document : parentSelector;

	const parents = [];
	var parent = $element.parentNode;
	while (parent !== selector) {
		const parentCopy = parent;
		parents.push(parentCopy);
		parent = parentCopy.parentNode;
	}
	parents.push(selector);
	return parents;
}

function getParent($element, selector) {
	const parents = getParents($element, selector);

	if (parents.length > 0) {
		return parents.find(filterParentElement, selector);
	}
}

function filterParentElement(selector) {
	return this === selector;
}

function isInViewport(element) {
	const rect = element.getBoundingClientRect();
	const html = document.documentElement;
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <= (window.innerHeight || html.clientHeight) &&
		Math.trunc(rect.right) <= (window.innerWidth || html.clientWidth)
	);
}

/**
 * Creates a HTML element, adds attributes and optionally attaches to a parent element.
 * @template {keyof HTMLElementTagNameMap} T
 * @param {T} tagName
 * @param {{[key: string]: any}} attributes
 * @param {HTMLElement | null | undefined} $parentElement
 * @returns {HTMLElementTagNameMap[T]} the created element
 */
function createElementAsChild(tagName, attributes, $parentElement) {
	const $element = document.createElement(tagName);

	if (attributes) {
		for (const attribute in attributes) {
			if (Object.hasOwn(attributes, attribute)) {
				$element.setAttribute(attribute, attributes[attribute]);
			}
		}
	}

	if ($parentElement) {
		$parentElement.appendChild($element);
	}
	return $element;
}

/**
 * Converts an HTML string into an Element.
 * @param {string} htmlString
 * @returns {DocumentFragment | null}
 */
function parseHTMLString(htmlString) {
	if (isNullishOrEmpty(htmlString)) {
		return null;
	}

	const template = document.createElement("template");
	template.innerHTML = htmlString.trim();
	const fragment = template.content;

	return fragment;
}

/* Since we are replacing JQuery, we have only two options for creating global fade in and fade out effects right now.
 they are to use important or to set style attribute in code.
 Out of these two, we choose the lesser evil, that is style attribute.*/
function fadeIn(el, display) {
	el.style.opacity = 0.7;
	el.style.display = display || "block";
}

function fadeOut(el) {
	el.style.opacity = 1;
	el.style.display = "none";
}

function removeElements($elements) {
	if ($elements) {
		$elements.forEach(($element) => {
			$element.remove();
		});
	}
}

/**
 * @deprecated use `string.endsWith`
 */
function endsWith(source, searchString, position) {
	const subjectString = source;
	var pos = position;
	if (typeof pos !== "number" || !isFinite(pos) || Math.floor(pos) !== pos || pos > subjectString.length) {
		pos = subjectString.length;
	}
	pos -= searchString.length;
	const lastIndex = subjectString.lastIndexOf(searchString, pos);
	return lastIndex !== -1 && lastIndex === pos;
}

/**
 * Gets a string value attribute from an element.
 * @param {HTMLElement} $element
 * @param {string} attribute
 * @returns
 */
function getStringAttribute($element, attribute) {
	return $element.getAttribute(attribute);
}

/**
 * Sets a string value attribute on an element.
 * @param {HTMLElement} $element
 * @param {string} attribute
 * @param {string} value
 * @returns
 */
function setStringAttribute($element, attribute, value) {
	$element.setAttribute(attribute, value);
}

/**
 * Gets an attribute from an element and converts it to boolean.
 * @param {HTMLElement} $element
 * @param {string} attribute
 * @returns {boolean}
 */
function getBooleanAttribute($element, attribute) {
	const value = getStringAttribute($element, attribute);

	return value?.toLowerCase() === "true";
}

/**
 * Sets a boolean attribute on an element but converts it to string first.
 * @param {HTMLElement} $element
 * @param {string} attribute
 * @param {boolean} value
 * @returns
 */
function setBooleanAttribute($element, attribute, value) {
	setStringAttribute($element, attribute, `${value}`);
}

/**
 * @param {HTMLElement} $element
 * @returns
 */
function getAriaExpanded($element) {
	return getBooleanAttribute($element, "aria-expanded");
}
/**
 * @param {HTMLElement} $element
 * @param {string|boolean} value
 * @returns
 */
function setAriaExpanded($element, value) {
	setBooleanAttribute($element, "aria-expanded", value);
}

/**
 * @param {HTMLElement} $element
 * @returns
 */
function getAriaControls($element) {
	return getStringAttribute($element, "aria-controls");
}

/**
 * @param {HTMLElement} $element
 * @param {string} value
 * @returns
 */
function setAriaControls($element, value) {
	setStringAttribute($element, "aria-controls", value);
}

/**
 * @param {HTMLElement} $element
 * @returns
 */
function getAriaSelected($element) {
	return getBooleanAttribute($element, "aria-selected");
}

/**
 * @param {HTMLElement} $element
 * @param {boolean} value
 * @returns
 */
function setAriaSelected($element, value) {
	setBooleanAttribute($element, "aria-selected", value);
}

/**
 * @param {HTMLElement} $element
 * @returns
 */
function getAriaHidden($element) {
	return getBooleanAttribute($element, "aria-hidden");
}

/**
 * @param {HTMLElement} $element
 * @param {boolean} value
 * @returns
 */
function setAriaHidden($element, value) {
	setBooleanAttribute($element, "aria-hidden", value);
}

function getXylElement($element) {
	return getStringAttribute($element, "xyl-element");
}

/**
 * Returns a dataset item as a string.
 * @param {HTMLElement} $element
 * @param {string} key
 * @returns {string}
 * @private
 */
function getDatasetItem($element, key) {
	const item = $element?.dataset[key] ?? "";

	return item;
}

/**
 * Returns a string type dataset item with an optional default value.
 * @template {string | number | boolean} T
 * @param {HTMLElement} $element
 * @param {string} key
 * @param {T} defaultValue
 * @returns {T} value or default
 * @private
 */
function getParsedDatasetItem($element, key, defaultValue) {
	if (isNullish($element)) {
		return defaultValue;
	}

	const item = getDatasetItem($element, key);

	if (isNullishOrEmpty(item)) {
		return defaultValue;
	}

	if (typeof defaultValue === "boolean") {
		return item.toLowerCase() === "true";
	}

	if (typeof defaultValue === "number") {
		return safeParseNumber(item, defaultValue);
	}

	return item;
}

/**
 * Returns a boolean type dataset item with an optional default value.
 * Will try to parse the value to a boolean.
 * @param {HTMLElement} $element
 * @param {string} key
 * @param {boolean} defaultValue
 * @returns
 */
function getDatasetItemBoolean($element, key, defaultValue = false) {
	return getParsedDatasetItem($element, key, defaultValue);
}

/**
 * Returns a number type dataset item with an optional default value.
 * Will try to parse the value to a number.
 * @param {HTMLElement} $element
 * @param {string} key
 * @param {number} defaultValue
 * @returns
 */
function getDatasetItemNumber($element, key, defaultValue = 0) {
	return getParsedDatasetItem($element, key, defaultValue);
}

/**
 * Returns a string type dataset item with an optional default value.
 * @param {HTMLElement} $element
 * @param {string} key
 * @param {string} defaultValue
 * @returns
 */
function getDatasetItemString($element, key, defaultValue = "") {
	return getParsedDatasetItem($element, key, defaultValue);
}

/**
 * Checks if the event target is a specified tag name.
 * @template {keyof HTMLElementTagNameMap} T
 * @param {HTMLElement | EventTarget | null} element - The target of the event.
 * @param {T} tagName - The tag name to check against (e.g. 'button', 'div').
 * @returns {element is HTMLElementTagNameMap[T]} True if the target element is of the specified tag name.
 */
function isTagOfType(element, tagName) {
	return element instanceof HTMLElement && element.tagName.toLowerCase() === tagName.toLowerCase();
}

export {
	addClass,
	addListener,
	createElementAsChild,
	parseHTMLString,
	endsWith,
	fadeIn,
	fadeOut,
	getAriaControls,
	getAriaExpanded,
	getAriaHidden,
	getAriaSelected,
	getDatasetItemBoolean,
	getDatasetItemNumber,
	getDatasetItemString,
	getNextSibling,
	getParent,
	getParents,
	getPreviousSibling,
	getSiblings,
	getSiblingsByType,
	getVisibleElements,
	getXylElement,
	height,
	hideElement,
	hideElements,
	isInViewport,
	isTagOfType,
	isVisible,
	outerHeight,
	removeClass,
	removeElements,
	setAriaControls,
	setAriaExpanded,
	setAriaHidden,
	setAriaSelected,
	showElement,
	showElements,
	toggleVisibility,
	width,
};
